import { Injectable } from '@angular/core';
import { DateValidModel, PtLuxonDate } from '../../models/projects/date.models';
import {
  DailyLogOperationInstance,
  DailyOperationLogRecord
} from '../../models/projects/operation.models';
import { cloneDeep } from 'lodash';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { DateLostInTimeResult } from '../../models/time-zones/time-zone.models';
import { CurrentDayService } from './current-day.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { DateTime, DurationObject, Settings } from 'luxon';

@Injectable()
export class DateService {

  constructor(
    private timeZoneService: TimeZoneService,
    private currentDayService: CurrentDayService,
    private dateParserService: DateParserService
  ) {

  }

  public async addDuration(dateTime: DateTime, duration: DurationObject, keepLocalTime?: boolean): Promise<DateTime> {
    dateTime = dateTime.plus(duration);

    const tz = await this.timeZoneService.getEditorLocally({dateTime: dateTime, dateAmbiguous: false});

    if (tz) {
      const offsetString = PtLuxonDate.getOffsetString(tz.secondsOffsetFromUtc);
      // setZone() will not only set the offset of the DateTime, but also apply the offset to the date
      dateTime = dateTime.setZone(offsetString, {keepLocalTime: keepLocalTime});
    }

    return dateTime;
  }

  public async minusDuration(dateTime: DateTime, duration: DurationObject, keepLocalTime?: boolean): Promise<DateTime> {
    dateTime = dateTime.minus(duration);

    const tz = await this.timeZoneService.getEditorLocally({dateTime: dateTime, dateAmbiguous: false});

    if (tz) {
      const offsetString = PtLuxonDate.getOffsetString(tz.secondsOffsetFromUtc);
      // setZone() will not only set the offset of the DateTime, but also apply the offset to the date
      dateTime = dateTime.setZone(offsetString, {keepLocalTime: keepLocalTime});
    }

    return dateTime;
  }


  nullCheckSelectedOffset(ptLuxonDate: PtLuxonDate) {
    return ptLuxonDate && ptLuxonDate.multipleOffsets && ptLuxonDate.multipleOffsets.selectedOffsetMinutes != null
      ? ptLuxonDate.multipleOffsets.selectedOffsetMinutes
      : null;
  }

  // to set the zone specifically on a DateTime, we need to construct a string as below
  createLuxonOffsetString(secondsOffset: number): string {
    let offsetString = '';

    if (secondsOffset > 0) {
      offsetString = 'utc+' + (secondsOffset / 3600); // convert seconds to hours
    } else if (secondsOffset < 0) {
      offsetString = 'utc-' + (secondsOffset / 3600);
    } else {
      offsetString = 'utc';
    }

    return offsetString;
  }

  async setPtLuxonDateFromUserEnteredString(time: string, baseDate: DateTime, previousOffsetMinutes?: number): Promise<PtLuxonDate> {
    const ptLuxonDate = new PtLuxonDate();

    if (!time) {
      ptLuxonDate.setDate(null);
      return ptLuxonDate;
    }

    Settings.defaultZone = 'utc';
    const index = time.indexOf(':');
    const hours = time.substring(index - 2, index);
    const minutes = time.substring(index + 1, index + 3);

    const tempDate = baseDate || DateTime.local();

    ptLuxonDate.dateTime = DateTime.fromObject({
      year: tempDate.year,
      month: tempDate.month,
      day: tempDate.day,
      hour: +hours,
      minute: +minutes,
    }, {zone: ptLuxonDate.dateTime.zoneName});

    const timeZone = await this.timeZoneService.getEditorLocally({dateTime: ptLuxonDate.dateTime, dateAmbiguous: true});

    if (!timeZone) {
      return ptLuxonDate;
    }

    // date.dateTime is local here, so set dateAmbiguous = true
    await ptLuxonDate.checkForMultipleOffsets(this.timeZoneService);

    if (ptLuxonDate.multipleOffsets && ptLuxonDate.multipleOffsets.result) {
      return ptLuxonDate;
    }

    ptLuxonDate.isLostInTime = this.timeZoneService.checkForDateLostInTime(ptLuxonDate, timeZone)

    ptLuxonDate.setOffset(timeZone.secondsOffsetFromUtc, true);

    return ptLuxonDate;
  }

  async incrementPtLuxonDateFromUserEnteredString(time: string, baseDate: DateTime): Promise<PtLuxonDate> {
    const startOfNextDay = await this.getEndOfDayLocally(baseDate);
    return this.setPtLuxonDateFromUserEnteredString(time, startOfNextDay.dateTime);
  }

  async convertLocalJSDateToDateTime(date: Date): Promise<DateTime> {
    let dateClone = cloneDeep(date) as Date;
    dateClone = new Date(dateClone.getTime() - dateClone.getTimezoneOffset() * 60000);

    let dateTime = DateTime.fromJSDate(dateClone);
    const tz = await this.timeZoneService.getEditorLocally({dateTime: dateTime, dateAmbiguous: false});

    if (tz) {
      const offsetString = PtLuxonDate.getOffsetString(tz.secondsOffsetFromUtc);
      // setZone() will not only set the offset of the DateTime, but also apply the offset to the date
      dateTime = dateTime.setZone(offsetString, {keepLocalTime: true});
    }

    return dateTime;
  }

  convertDateTimeToLocalJSDate(dateTime: DateTime): Date {
    const utcDateTime = dateTime.setZone('utc', {keepLocalTime: true})
    return new Date(utcDateTime.toJSDate().getTime() + utcDateTime.toJSDate().getTimezoneOffset() * 60000);
  }

  setTimeStringFromPtLuxonDate(ptLuxonDate: PtLuxonDate) {
    if (!ptLuxonDate) {
      return null;
    }

    return this.setTimeStringFromDateTime(ptLuxonDate.dateTime);
  }

  setTimeStringFromDateTime(dateTime: DateTime) {
    if (!dateTime) {
      return null;
    }

    return dateTime.toFormat('HH:mm');
  }

  setDateTimeSeconds(dateTime: DateTime, seconds: number): DateTime {
    return DateTime.fromObject({
      year: dateTime.year,
      month: dateTime.month,
      day: dateTime.day,
      hour: dateTime.hour,
      minute: dateTime.minute,
      second: seconds,
      millisecond: 0,
    }, {zone: dateTime.zoneName});
  }

  setFullTimeString(dateTime: DateTime, includeDate: boolean = true): string {
    if (!dateTime) {
      return null;
    }

    const offset = dateTime.offset / 60; // .offset is minutes, so convert to hours
    let offsetString = '';

    if (offset >= 0) {
      offsetString = ` (+${offset})`;
    } else if (offset < 0) {
      offsetString = ` (${offset})`;
    }

    return dateTime.toFormat(includeDate ? 'dd/MM/yyyy, HH:mm' : 'HH:mm') + offsetString;
  }

  compareDayOffsetsPreApplied(firstDate: DateTime, secondDate: DateTime): boolean {
    if (!firstDate || !secondDate) {
      return false;
    }

    return (firstDate.year === secondDate.year
      && firstDate.month === secondDate.month
      && firstDate.day === secondDate.day);
  }

  isFirstDateLargerOffsetsPreApplied(firstDate: DateTime, secondDate: DateTime) {
    if (!firstDate || !secondDate) {
      return false;
    }

    return firstDate > secondDate;
  }

  isFirstDayLargerOffsetsPreApplied(firstDate: DateTime, secondDate: DateTime): boolean {
    if (!firstDate || !secondDate) {
      return false;
    }

    return (firstDate.year > secondDate.year
      || firstDate.month > secondDate.month
      || (firstDate.year == secondDate.year && firstDate.month == secondDate.month && firstDate.day > secondDate.day));
  }

  largerDate(firstDate: DateTime, secondDate: DateTime): DateTime {
    if ((!firstDate || isNaN(firstDate.toMillis())) && (!secondDate || isNaN(secondDate.toMillis()))) {
      return null;
    }

    if (!firstDate || isNaN(firstDate.toMillis())) {
      return secondDate;
    }

    if (!secondDate || isNaN(secondDate.toMillis())) {
      return firstDate;
    }

    return firstDate > secondDate ? firstDate : secondDate;
  }

  millisecondDifference(firstDate: DateTime, secondDate: DateTime): number {
    if (!firstDate || !secondDate) {
      return null;
    }

    return (firstDate.toMillis() - secondDate.toMillis());
  }

  setSecondsFromTimeShow(timeShow: string): number {
    const timeShowSplit = timeShow.split(':'); // split it at the colons
    return (+timeShowSplit[0]) * 60 * 60 + (+timeShowSplit[1]) * 60;
  }

  removeMilliseconds(date: Date): Date {
    date.setMilliseconds(0);
    return date;
  }

  // Removes seconds and milliseconds from a Date
  sanitizeDate(date: DateTime): DateTime {
    if (date !== null) {
       return date.set({
        year: date.year,
        month: date.month,
        day: date.day,
        hour: date.hour,
        minute: date.minute,
        second: 0,
        millisecond: 0
      });
    }

    return date;
  }

  // checks a time string is a valid date
  async timeAllowed(timeShow: string, dateTime: DateTime): Promise<boolean> {
    if (timeShow === null || timeShow === undefined || timeShow === '') {
      return true;
    }

    if (timeShow.includes('_')) {
      return false;
    }

    const checker = await this.setPtLuxonDateFromUserEnteredString(timeShow, dateTime);

    if (checker != null) {
      return !!checker.dateTime && !isNaN(checker.dateTime.toJSDate().getTime());
    }

    return null;
  }

  async validateDateTime(timeShow: string, luxonDate: DateTime): Promise<DateValidModel> {
    if (this.timeIsValid(timeShow) === false || timeShow === '') {
      return {isValid: false};
    }

    const ptLuxonDate = await this.setPtLuxonDateFromUserEnteredString(timeShow, luxonDate);

    if (ptLuxonDate != null) {
      return {
        ptLuxonDate: ptLuxonDate,
        isValid: ptLuxonDate.dateTime.toJSDate().getDate() && !isNaN(ptLuxonDate.dateTime.toJSDate().getTime())};
    }

    return {isValid: false};
  }

  timeInRange(startTime: DateTime, endTime: DateTime, chosenTime: DateTime): boolean {
    if (!endTime && !chosenTime) {
      return true;
    }

    if (!chosenTime || isNaN(chosenTime.toMillis())) {
      return false;
    }

    if (chosenTime > startTime && chosenTime < endTime) {
      return true;
    }

    if (chosenTime < endTime && (!chosenTime || isNaN(chosenTime.toMillis()))) {
      return true;
    }

    if (!endTime && startTime < chosenTime) {
      return true;
    }

    if (!endTime) {
      return false;
    }

    if (!startTime) {
      return false;
    }

    if (Math.abs(chosenTime.toMillis() - endTime.toMillis()) < 1000
      && (Math.abs(endTime.toMillis() - startTime.toMillis()) > 1000)
      && chosenTime.hour === 0
      && chosenTime.minute === 0) {
      return true;
    }

    return false;
  }

  timeInRangeInclusive(startTime: DateTime, endTime: DateTime, chosenTime: DateTime): boolean {
    if (!endTime && !chosenTime) {
      return true;
    }

    if (!chosenTime || isNaN(chosenTime.toMillis())) {
      return false;
    }

    if (chosenTime >= startTime && chosenTime <= endTime) {
      return true;
    }

    if (chosenTime <= endTime && (!chosenTime || isNaN(chosenTime.toMillis()))) {
      return true;
    }

    if (!endTime && startTime <= chosenTime) {
      return true;
    }

    if (!endTime) {
      return false;
    }

    if (!startTime) {
      return false;
    }

    if (Math.abs(chosenTime.toMillis() - endTime.toMillis()) < 1000
      && (Math.abs(endTime.toMillis() - startTime.toMillis()) > 1000)
      && chosenTime.toUTC().hour === 0
      && chosenTime.minute === 0) {
      return true;
    }

    return false;
  }

  selectEarliestDate(date1: DateTime, date2: DateTime): DateTime {
    if (!date1 || !date2) {
      return null;
    }

    if (date1 < date2) {
      return date1;
    }

    return date2;
  }

  /* Used for checking whether the time string that a user has entered is fully complete, and matches a time regex, eg 15:00 instead of 15:__ */
  timeIsValid(enteredTime: string, notNull?: boolean, allow24Hours?: boolean): boolean {
    if (allow24Hours) {
      if (/^([01]?[0-9]|2[0-4]):[0-5][0-9]$/.test(enteredTime) === false && enteredTime !== '' && enteredTime !== null) {
        return false;
      }
    } else {
      if (/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(enteredTime) === false && enteredTime !== '' && enteredTime !== null) {
        return false;
      }
    }

    if (notNull) {
      if (!enteredTime || enteredTime.trim() === '') {
        return false;
      }
    }

    return true;
  }

  timeFallsWithin(enteredTime: DateTime, startTime: DateTime, endTime?: DateTime): boolean {
    if (enteredTime >= startTime) {
      if (endTime == null || (endTime && enteredTime <= endTime)) {
        return true;
      }
    }

    return false;
  }

  async getStartOfDay(date: DateTime): Promise<DateTime> {
    if (!date) {
      return;
    }

    return await this.timeZoneService.getStartOfDayForDate(date);
  }

  async getEndOfDay(date: DateTime): Promise<DateTime> {
    if (!date) {
      return;
    }

    return await this.timeZoneService.getEndOfDayForDate(date);
  }

  async getEndOfDayLocally(dateTime: DateTime): Promise<PtLuxonDate> {
    if (!dateTime) {
      return;
    }

    // add 24 hours
    const roughEndOfDay = dateTime.plus({day: 1});
    let correctEndOfDay = new PtLuxonDate(roughEndOfDay);

    const endOfDayTimeZone = await this.timeZoneService.getEditorLocally({dateTime: roughEndOfDay});

    if (endOfDayTimeZone) {
      correctEndOfDay.setOffset(endOfDayTimeZone.secondsOffsetFromUtc, true);
    }

    return correctEndOfDay;
  }

  // adds 24 hours to a date, but also takes timezone changes into account when doing so - note that this means that the
  // dateTime offset may change, but the time string will not, meaning that this will not just add 24 hrs,
  // for example, if a tz change from +1 to +2 has been made between the start date and the incremented date, then
  // only 23 hours get added
  async incrementDay(ptLuxonDate: PtLuxonDate): Promise<PtLuxonDate> {
    await ptLuxonDate.addDurationToDateTime({day: 1}, this, this.timeZoneService, true);
    return ptLuxonDate;
    // // When a date is entered that is meant to be for the next day, the 'lostInTime' part of the ptDate no longer applies, as it is assuming
    // // that the date object is set for this day, when actually we are about to increment it by 1 day. Because of this, if the entered date
    // // is lost in time, we will get the wrong date when calculating the 'difference' below, unless we use the previous timezone offset
    // const startLostInTime = await this.timeZoneService.isDateLostInTime(ptDate.dateTime, dateIsLocal);
    // const startTimeZone = startLostInTime.timeZone;
    //
    // ptDate.date = new Date(ptDate.date.getTime() + 86400000);
    // const endTimeZone = await this.timeZoneService.getEditorLocally({date: ptDate.date});
    //
    // // if lost in time, use the previous timezone offset when calculating difference
    // const difference = endTimeZone.secondsOffsetFromUtc - (startLostInTime.result
    //   ? startTimeZone.previousSecondsOffsetFromUtc
    //   : startTimeZone.secondsOffsetFromUtc);
    // ptDate.date = new Date(ptDate.date.getTime() - difference * 1000);
    //
    // await ptDate.setLocalDate(this.timeZoneService, null);
    // ptDate.isLostInTime = await this.timeZoneService.isDateLostInTime(ptDate.date, null);
    //
    // return ptDate;
  }

  async setPtLuxonDateFromApi(ptLuxonDate: PtLuxonDate) {
    if (ptLuxonDate) {
      ptLuxonDate = await this.dateParserService.deserializePtLuxonDate(ptLuxonDate, this.timeZoneService);
    }

    return ptLuxonDate;
  }
}



// WEBPACK FOOTER //
// ./src/services/date/date.service.ts